<template>
    <div class="change-request" :id="id" ref="changeRequestContainer">
        <MessageBox
            v-if="showMessageBox"
            class="change-request-messagebox"
            :visible="true"
            type="success"
            :title="messageBoxTitle"
            :message="messageBoxText"
        />
        <MessageBox
            v-if="requestFailed"
            class="change-request-messagebox"
            :visible="true"
            type="error"
            :title="errorTitleText"
            :message="errorBodyText"
        />
        <h5>{{ headline }}</h5>
        <DTInput
            class="change-request-input"
            ref="changeRequestInput"
            :id="id"
            :placeholder="currentPlaceholder"
            :autoHeight="true"
            :speechToText="false"
            payload="changeRequest"
            @input="handleInput"
            @focus="stopTypewriter"
        />
        <DTButton
            class="change-request-button"
            :text="internalButtonText"
            :icon="internalButtonIcon"
            :iconPosition="buttonIconPosition"
            :externalLoading="loading"
            :disabled="loading || !book || changeRequest === '' || changesLeft <= 0"
            @click="submitChangeRequest"
        />
    </div>
</template>

<script>
import MessageBox from "./MessageBox.vue";
import DTInput from "./DTInput.vue";
import DTButton from "./DTButton.vue";
import EventBus from "../event-bus";
import axios from "axios";
import { getDraftIdFromContext, getDraftPayloadFromResponse } from "../utils/draftService";
import { safeGetJson } from "../utils/safeStorage";
import { CHANGE_REQUEST_POLL_CANCELLED, runChangeRequestPolling } from "../utils/changeRequestPolling";
import { getPollErrorDiagnostics } from "../utils/previewPollingResilience";
import { captureCustomizerMessage } from "../utils/sentry";
import { builderText, customizerText } from "../utils/customizerTexts";

const hasChangeCounter = (payload) =>
    Object.prototype.hasOwnProperty.call(payload || {}, "changeCounter");

export default {
    name: "ChangeRequest",
    components: {
        MessageBox,
        DTInput,
        DTButton,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        showMessageBox: {
            type: Boolean,
            default: true,
        },
        messageBoxTitle: {
            type: String,
            default: "",
        },
        messageBoxText: {
            type: String,
            default: "",
        },
        errorMessageTitle: {
            type: String,
            default: "",
        },
        errorMessageText: {
            type: String,
            default: "",
        },
        headline: {
            type: String,
            default: "",
        },

        placeholders: {
            type: Array,
            default: () => [],
        },
        typewriterDelay: {
            type: Number,
            default: 50,
        },
        pauseBetweenPlaceholders: {
            type: Number,
            default: 2000,
        },
        buttonText: {
            type: String,
            default: "Geschichte anpassen",
        },
        buttonIcon: {
            type: String,
            default: "",
        },
        buttonIconPosition: {
            type: String,
            default: "",
        },
        requestUrl: {
            type: String,
            default: "",
            required: true,
        },
        requestMode: {
            type: String,
            default: "fetch",
            validator: (value) => ["fetch", "poll"].includes(value),
        },
        pollStartUrl: {
            type: String,
            default: "",
        },
        pollStatusUrl: {
            type: String,
            default: "",
        },
        pollIntervalMs: {
            type: Number,
            default: 3000,
            validator: (value) => value >= 500,
        },
        pollTimeoutMs: {
            type: Number,
            default: 900000,
            validator: (value) => value >= 0,
        },
        additionalPayloadSent: {
            type: String,
            default: "",
        },
        maxChanges: {
            type: Number,
            default: 5,
        },
    },
    inject: ['pageSettings'],
    data() {
        return {
            changesLeft: this.maxChanges,
            changeRequest: "",
            internalButtonText: this.buttonText,
            internalButtonIcon: this.buttonIcon,
            currentPage: 1,
            book: null,
            loading: false,
            requestFailed: false,
            currentPlaceholder: "",
            placeholderIndex: 0,
            typewriterInterval: null,
            typewriterTimeout: null,
            isTyping: false,
            isErasing: false,
            charIndex: 0,
            allPlaceholders: [],
            observer: null,
            isVisible: false,
            hasBackendChangeCounter: false,
            changeCounter: null,
            isUnmounted: false
        };
    },
    mounted() {
        EventBus.on("previewSlideChanged", this.setCurrentPage);
        EventBus.on("bookLoaded", this.bookLoaded);
        
        this.allPlaceholders = this.placeholders.length > 0 
            ? this.placeholders.map(item => item.placeholder) 
            : [];
        
        this.internalButtonText = this.buttonText;
        
        this.$nextTick(() => {
            this.setupVisibilityObserver();
        });
    },
    beforeUnmount() {
        this.isUnmounted = true;
        EventBus.off("previewSlideChanged", this.setCurrentPage);
        EventBus.off("bookLoaded", this.bookLoaded);
        this.stopTypewriter();
        
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    computed: {
        errorTitleText() {
            return builderText(this.errorMessageTitle, "changeErrorTitle");
        },
        errorBodyText() {
            return builderText(this.errorMessageText, "changeErrorText");
        },
    },
    methods: {
        setupVisibilityObserver() {
            this.observer = new IntersectionObserver((entries) => {
                const isVisible = entries[0].isIntersecting;
                
                if (isVisible && !this.isVisible) {
                    this.isVisible = true;
                    
                    if (this.allPlaceholders.length > 0 && this.changeRequest === '') {
                        this.startTypewriter();
                    }
                } else if (!isVisible && this.isVisible) {
                    this.isVisible = false;
                    this.stopTypewriter();
                }
            }, { threshold: 0.1 });
            
            if (this.$refs.changeRequestContainer) {
                this.observer.observe(this.$refs.changeRequestContainer);
            }
        },
        setCurrentPage(page) {
            this.currentPage = `page${page}`;
        },
        bookLoaded(book) {
            this.book = book;
            this.updateChangeCounterFromPayload(book);
        },
        updateChangeCounterFromPayload(responseData) {
            const payload = getDraftPayloadFromResponse(responseData);

            if (!hasChangeCounter(payload)) {
                return;
            }

            this.changeCounter = payload.changeCounter;
            this.hasBackendChangeCounter = true;
        },
        startTypewriter() {
            this.stopTypewriter();
            
            if (this.allPlaceholders.length === 0 || !this.isVisible) return;
            
            this.isTyping = true;
            this.isErasing = false;
            
            const startTyping = () => {
                const currentText = this.allPlaceholders[this.placeholderIndex];
                this.charIndex = 0;
                this.currentPlaceholder = "";
                
                this.typewriterInterval = setInterval(() => {
                    if (this.charIndex < currentText.length) {
                        this.currentPlaceholder += currentText.charAt(this.charIndex);
                        this.charIndex++;
                    } else {
                        clearInterval(this.typewriterInterval);
                        
                        this.typewriterTimeout = setTimeout(() => {
                            this.startErasing();
                        }, this.pauseBetweenPlaceholders);
                    }
                }, this.typewriterDelay);
            };
            
            startTyping();
        },
        startErasing() {
            this.isTyping = false;
            this.isErasing = true;
            
            const currentText = this.allPlaceholders[this.placeholderIndex];
            const totalEraseTime = 800;
            const charactersToErase = currentText.length;
            const delayPerCharacter = Math.max(20, Math.min(50, totalEraseTime / charactersToErase));
            
            this.typewriterInterval = setInterval(() => {
                if (this.currentPlaceholder.length > 0) {
                    this.currentPlaceholder = this.currentPlaceholder.slice(0, -1);
                } else {
                    clearInterval(this.typewriterInterval);
                    this.isErasing = false;
                    
                    this.placeholderIndex = (this.placeholderIndex + 1) % this.allPlaceholders.length;
                    this.startTypewriter();
                }
            }, delayPerCharacter);
        },
        stopTypewriter() {
            if (this.typewriterInterval) {
                clearInterval(this.typewriterInterval);
                this.typewriterInterval = null;
            }
            
            if (this.typewriterTimeout) {
                clearTimeout(this.typewriterTimeout);
                this.typewriterTimeout = null;
            }
            
            this.isTyping = false;
            this.isErasing = false;
        },
        handleInput(event) {
            this.changeRequest = event.target.value;
            
            if (this.changeRequest.trim() !== '') {
                this.stopTypewriter();
            } else {
                if (this.isVisible) {
                    this.startTypewriter();
                }
            }
        },
        reportPollFailure(error) {
            const diagnostics = getPollErrorDiagnostics(error);
            captureCustomizerMessage("customizer_change_request_failed", {
                level: "error",
                tags: {
                    component: "ChangeRequest",
                    feature: "change_request",
                    request_mode: "poll",
                    failure_reason: diagnostics.failureReason || "request_failed",
                    poll_phase: diagnostics.pollPhase || "none",
                    job_id: diagnostics.jobId || "unknown",
                    http_status: diagnostics.httpStatus || "none",
                    axios_code: diagnostics.axiosCode || "none",
                    elapsed_ms: diagnostics.elapsedMs ?? "unknown",
                },
            });
        },
        submitChangeRequest() {
            this.requestFailed = false;
            // The button stays disabled until the preview has loaded.
            if (!this.book) return;

            EventBus.emit("changeRequestSubmitted");
            this.loading = true;

            let additionalPayload = {};
            if (this.additionalPayloadSent) {
                const [storageKey, ...path] =
                    this.additionalPayloadSent.split(".");

                const storedData = safeGetJson(storageKey, {});

                const getNestedObject = (obj, pathArray) => {
                    if (pathArray.length === 0) return obj;
                    const key = pathArray[0];
                    const nestedObj = obj[key];

                    if (nestedObj) {
                        return {
                            [key]: getNestedObject(
                                nestedObj,
                                pathArray.slice(1)
                            ),
                        };
                    } else {
                        return null;
                    }
                };

                additionalPayload = getNestedObject(storedData, path);
            }
            
            const variantId = this.pageSettings.variant || "";
            
            const draftId = getDraftIdFromContext();
            
            const requestPayload = {
                changeprompt: this.changeRequest,
                changepage: this.currentPage,
                variantID: variantId,
                currentUrl: window.location.href,
                pages: this.book.pages,
                additionalPayload,
            };

            if (this.hasBackendChangeCounter) {
                requestPayload.changeCounter = this.changeCounter;
            }
            
            if (this.book.output && this.book.output.song) {
                requestPayload.output = {
                    song: this.book.output.song
                };
            }
            
            if (draftId) {
                requestPayload.draftId = draftId;
            }

            // Long change requests break on mobile connections, so poll mode starts a
            // backend job and asks for its status, like the preview does.
            const usePolling = this.requestMode === "poll" && this.pollStartUrl && this.pollStatusUrl;
            const changeRequestPromise = usePolling
                ? runChangeRequestPolling({
                    requestBody: requestPayload,
                    pollStartUrl: this.pollStartUrl,
                    pollStatusUrl: this.pollStatusUrl,
                    pollIntervalMs: this.pollIntervalMs,
                    pollTimeoutMs: this.pollTimeoutMs,
                    isActive: () => !this.isUnmounted,
                })
                : axios
                    .post(
                        this.requestUrl,
                        requestPayload,
                        {
                            headers: {
                                "Content-Type": "application/json",
                            },
                            customizerFeature: "change_request",
                        }
                    )
                    .then((response) => response.data);

            changeRequestPromise
                .then((responseData) => {
                    if (this.isUnmounted) return;
                    this.updateChangeCounterFromPayload(responseData);

                    this.changeRequest = "";
                    this.changesLeft -= 1;
                    this.$refs.changeRequestInput.resetValue();
                    EventBus.emit("changeRequestResponse", responseData);

                    if (this.isVisible) {
                        this.startTypewriter();
                    }
                })
                .catch((error) => {
                    if (error?.code === CHANGE_REQUEST_POLL_CANCELLED) return;
                    if (process.env.VUE_APP_DEV_MODE === "true") {
                        console.error("Change Request failed", {
                            code: error?.code || "unknown",
                            status: error?.response?.status || "none",
                        });
                    }
                    if (usePolling) {
                        // Poll requests skip the axios reporting, so report the final outcome once.
                        this.reportPollFailure(error);
                    }
                    // sentry-ignore: fetch mode is reported by installAxiosReporting (feature change_request)
                    // The change was not applied, so the customer keeps it and sees why nothing happened.
                    this.requestFailed = true;
                })
                .finally(() => {
                    if (this.isUnmounted) return;
                    this.loading = false;

                    if (this.changesLeft <= 0) {
                        this.internalButtonText = customizerText("noChangesLeft");
                    } else {
                        this.internalButtonText = this.buttonText;
                    }
                });
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;
.change-request {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    .change-request-messagebox {
        @media (max-width: $breakpoint-mobile) {
            order: 2;
            margin: 24px 0 0 !important;
        }
    }

    h5 {
        margin: 24px 0 0;
        text-align: center;
        font-family: "TT Commons Pro", sans-serif;

        @media (max-width: $breakpoint-mobile) {
            order: 1;
            margin: 0;
        }
    }

    .change-request-input {
        margin: 16px 0 0;
        width: 100%;

        @media (max-width: $breakpoint-mobile) {
            order: 3;
        }
        
        :deep(.dt-input) {
            @media (max-width: $breakpoint-mobile) {
                height: auto;
                min-height: 54px;
                white-space: normal;
                
                &::placeholder {
                    white-space: normal;
                    overflow: visible;
                }
            }
        }
    }

    .change-request-button {
        margin: 32px 0 0;

        @media (max-width: $breakpoint-mobile) {
            order: 4;
            width: 100%;
            margin: 24px 0 0;
        }
    }
}
</style>
